import React, { useState } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { agentInstanceService } from '../services/agentService'; 
import { fileService } from '../services/fileService';
import { ShowImage } from '../utils/ShowImage';


export interface S3Params{
  key:string;
  bucket:string
  contentType?:string
}

interface LogoUploaderProps {
  label?: string;
  value: S3Params | null;
  onChange: (value: S3Params | null) => void;
}

export const LogoUploader: React.FC<LogoUploaderProps> = ({ label = 'Chatbot Logo', value, onChange }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<any>({
    unit: '%',
    width: 70,
    aspect: 1, 
  });
  const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setSrc(reader.result as string);
      setShowCropper(true);
      setCrop({
        unit: '%',
        width: 70,
        aspect: 1,
      });
    };
    reader.readAsDataURL(file);
  };

  const onImageLoaded = (img: HTMLImageElement) => {
    setImageRef(img);
    return false;
  };

  const getCroppedBlob = async (): Promise<Blob | null> => {
    if (!imageRef || !crop.width || !crop.height) return null;

    const canvas = document.createElement('canvas');
    const scaleX = imageRef.naturalWidth / imageRef.width;
    const scaleY = imageRef.naturalHeight / imageRef.height;

    const pixelWidth =
      (crop.width as number) * (crop.unit === '%' ? imageRef.width / 100 : 1);
    const pixelHeight =
      (crop.height as number) * (crop.unit === '%' ? imageRef.height / 100 : 1);
    const pixelX =
      (crop.x as number) * (crop.unit === '%' ? imageRef.width / 100 : 1);
    const pixelY =
      (crop.y as number) * (crop.unit === '%' ? imageRef.height / 100 : 1);

    canvas.width = pixelWidth * scaleX;
    canvas.height = pixelHeight * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(
      imageRef,
      pixelX * scaleX,
      pixelY * scaleY,
      pixelWidth * scaleX,
      pixelHeight * scaleY,
      0,
      0,
      pixelWidth * scaleX,
      pixelHeight * scaleY
    );

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  };

  const handleApplyCrop = async () => {
    try {
      setUploading(true);

      const blob = await getCroppedBlob();
      if (!blob) {
        setShowCropper(false);
        setUploading(false);
        return;
      }

      const file = new File([blob], `chatbot-logo-${Date.now()}.png`, {
        type: 'image/png',
      });

      const response = await fileService.upload(file, {
        subdir: 'chatbot-logos', 
      });


      const uploadedUrl = response?.items?.[0]?.publicUrl ?? null;
      const uplodedKey= response?.items?.[0]?.key
      const uploadedBucket =response?.items?.[0]?.bucket
      onChange({
        key:uplodedKey,
        bucket:uploadedBucket,
        contentType:response?.items?.[0]?.contentType
      });

      setShowCropper(false);
    } catch (err) {
      console.error('Upload logo failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setSrc(null);
    setShowCropper(false);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-agentic-text-primary">
        {label} <span className="text-xs text-agentic-text-muted"></span>
      </label>

      <div className="flex items-center gap-4">
        {/* Preview */}
        <div className="w-16 h-16 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
          {value ? (
            <ShowImage 
              s3Params={value}
              alt="Chatbot logo" 
              className="w-full h-full object-cover" 
            />
          ) : (
            <span className="text-xs text-agentic-text-muted">Logo</span>
          )}
        </div>

        {/* Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 text-white bg-blue-700 hover:bg-blue-800 cursor-pointer">
            {uploading ? 'Uploading...' : 'Upload Here'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onSelectFile}
              disabled={uploading}
            />
          </label>
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 text-agentic-text-secondary hover:bg-gray-50"
              disabled={uploading}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Cropper modal */}
      {showCropper && src && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !uploading && setShowCropper(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-full max-w-lg mx-4">
            <h3 className="text-sm font-semibold text-agentic-text-primary mb-3">
              {uploading ? 'Uploading...' : 'Crop Logo'}
            </h3>

            <div className="max-h-[70vh] overflow-auto">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                // aspect={1}
                className="max-h-[400px]"
              >
                <img src={src} onLoad={(e) => onImageLoaded(e.currentTarget)} />
              </ReactCrop>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowCropper(false)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-agentic-text-secondary hover:bg-gray-50"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyCrop}
                className="px-3 py-1.5 rounded-lg text-xs text-white bg-agentic-primary-blue hover:bg-agentic-primary-blue/90 disabled:opacity-60"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Apply & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoUploader;
